// src/pages/OntologyEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Breadcrumbs,
  Link,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Folder as FolderIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Search as SearchIcon,
  Home as HomeIcon,
  Storage as StorageIcon,
  Schema as SchemaIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import Gateway from '../api/gateway';

// Common layout
import PageShell from '../components/common/PageShell';
import PagePanel from '../components/common/PagePanel';

// RBAC
import { isAdmin } from '../utils/accessControl';

// ============================================================================
// Константы
// ============================================================================
const ONTOLOGY_TYPES = {
  world_template: { label: 'Шаблон мира', color: '#f44336', icon: '🌍' },
  faction_template: { label: 'Шаблон фракции', color: '#9c27b0', icon: '🏛️' },
  character_template: { label: 'Шаблон персонажа', color: '#3f51b5', icon: '👤' },
  technology_tree: { label: 'Дерево технологий', color: '#009688', icon: '🔬' },
  resource_system: { label: 'Система ресурсов', color: '#ff9800', icon: '💰' },
  quest_system: { label: 'Система квестов', color: '#795548', icon: '🎯' },
  race_system: { label: 'Система рас', color: '#607d8b', icon: '👽' },
  skill_system: { label: 'Система навыков', color: '#2196f3', icon: '🎓' },
  market_system: { label: 'Рыночная система', color: '#4caf50', icon: '📊' },
  entity: { label: 'Сущность', color: '#ff5722', icon: '🔗' },
  collection: { label: 'Коллекция', color: '#673ab7', icon: '📚' },
  object: { label: 'Объект', color: '#00bcd4', icon: '📦' },
  custom: { label: 'Пользовательская', color: '#8bc34a', icon: '⚙️' },
};

// ============================================================================
// Утилиты
// ============================================================================
const formatValue = (value) => {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'object') {
    if (Array.isArray(value)) return `[${value.length} элементов]`;
    return `{${Object.keys(value).length} полей}`;
  }
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

// ============================================================================
// Хлебные крошки
// ============================================================================
function BreadcrumbNavigation({ path, onPathClick }) {
  const segments = path.split('/').filter(Boolean);
  return (
    <Breadcrumbs sx={{ mb: 1 }}>
      <Link
        component="button"
        variant="caption"
        onClick={() => onPathClick('')}
        sx={{ display: 'flex', alignItems: 'center', fontSize: '0.7rem' }}
      >
        <HomeIcon sx={{ mr: 0.5, fontSize: 14 }} />
        Корень
      </Link>
      {segments.map((segment, index) => (
        <Link
          key={index}
          component="button"
          variant="caption"
          onClick={() => onPathClick(segments.slice(0, index + 1).join('/'))}
          sx={{
            color: index === segments.length - 1 ? 'text.primary' : 'primary.main',
            fontWeight: index === segments.length - 1 ? 'bold' : 'normal',
            fontSize: '0.7rem',
          }}
        >
          {segment}
        </Link>
      ))}
    </Breadcrumbs>
  );
}

// ============================================================================
// Дерево онтологий
// ============================================================================
function OntologyTree({ treeData, onSelect, selectedPath, loading, searchTerm }) {
  const [openNodes, setOpenNodes] = useState(new Set(['files', 'mongodb']));
  const [filteredTree, setFilteredTree] = useState([]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTree(treeData);
      return;
    }
    const filterTree = (nodes) =>
      nodes
        .map((node) => {
          const matches =
            node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (node.ontology_type &&
              ONTOLOGY_TYPES[node.ontology_type]?.label
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()));
          let filteredChildren = node.children;
          if (node.children) filteredChildren = filterTree(node.children);
          if (matches || (filteredChildren && filteredChildren.length > 0)) {
            return { ...node, children: filteredChildren };
          }
          return null;
        })
        .filter(Boolean);
    setFilteredTree(filterTree(treeData));
  }, [treeData, searchTerm]);

  const toggleNode = (path) => {
    setOpenNodes((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const renderItem = (item, level = 0) => {
    const isOpen = openNodes.has(item.path);
    const isSelected = selectedPath === item.path;
    const hasChildren = item.children && item.children.length > 0;
    const typeConfig = ONTOLOGY_TYPES[item.ontology_type];

    return (
      <Box key={item.path}>
        <ListItemButton
          dense
          sx={{
            pl: 1 + level * 2,
            py: 0.25,
            backgroundColor: isSelected ? 'action.selected' : 'transparent',
            borderLeft: 3,
            borderColor: isSelected ? 'primary.main' : 'transparent',
          }}
          onClick={() => item.type === 'file' && onSelect(item.path, item)}
        >
          <ListItemIcon sx={{ minWidth: 28 }}>
            {item.type === 'directory' ? (
              <FolderIcon sx={{ color: isOpen ? 'warning.main' : 'warning.light', fontSize: 18 }} />
            ) : (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: typeConfig?.color || '#757575',
                  color: 'white',
                  fontSize: '0.65rem',
                }}
              >
                {typeConfig?.icon || '📄'}
              </Box>
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography
                variant="body2"
                noWrap
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  color: isSelected ? 'primary.main' : 'text.primary',
                }}
              >
                {item.name}
              </Typography>
            }
            secondary={
              item.type === 'file' ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  {typeConfig?.label || item.ontology_type}
                  {item.object_count > 0 && ` · ${item.object_count}`}
                </Typography>
              ) : null
            }
          />
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(item.path);
              }}
              sx={{ p: 0.25 }}
            >
              {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          )}
        </ListItemButton>
        {hasChildren && isOpen && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense sx={{ py: 0 }}>
              {item.children.map((child) => renderItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (!filteredTree || filteredTree.length === 0) {
    return (
      <Alert severity={searchTerm ? 'warning' : 'info'} sx={{ m: 1, fontSize: '0.7rem' }}>
        {searchTerm ? 'Ничего не найдено' : 'Нет доступных онтологий'}
      </Alert>
    );
  }
  return <List dense sx={{ py: 0 }}>{filteredTree.map((item) => renderItem(item))}</List>;
}

// ============================================================================
// Редактируемое поле (с readOnly)
// ============================================================================
function EditableField({
  label,
  value,
  onChange,
  type = 'text',
  multiline = false,
  required = false,
  canEdit = true,
}) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const [error, setError] = useState('');

  useEffect(() => setTempValue(value), [value]);

  const handleSave = () => {
    if (required && !tempValue) return setError('Поле обязательно');
    let finalValue = tempValue;
    if (type === 'number') {
      finalValue = parseFloat(tempValue);
      if (isNaN(finalValue)) return setError('Введите корректное число');
    } else if (type === 'boolean') {
      finalValue = tempValue === 'true';
    }
    setError('');
    onChange(finalValue);
    setEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setEditing(false);
    setError('');
  };

  // Объект/массив — только чтение
  if (typeof value === 'object' && value !== null) {
    return (
      <Box
        sx={{
          mb: 1,
          p: 0.75,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
          {label}
          {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
        </Typography>
        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: '0.75rem' }}>
          {formatValue(value)}
        </Typography>
      </Box>
    );
  }

  if (editing && canEdit) {
    return (
      <Box sx={{ mb: 1 }}>
        <TextField
          label={label}
          value={tempValue}
          onChange={(e) => {
            setTempValue(e.target.value);
            setError('');
          }}
          fullWidth
          multiline={multiline}
          rows={multiline ? 2 : 1}
          type={type}
          size="small"
          error={!!error}
          helperText={error}
          required={required}
          sx={{ '& input, & textarea': { fontSize: '0.75rem' } }}
        />
        <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
          <Button size="small" variant="contained" onClick={handleSave} sx={{ fontSize: '0.65rem', py: 0.25 }}>
            Сохранить
          </Button>
          <Button size="small" onClick={handleCancel} sx={{ fontSize: '0.65rem', py: 0.25 }}>
            Отмена
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mb: 1,
        p: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:hover': canEdit
          ? { borderColor: 'primary.main', backgroundColor: 'action.hover' }
          : {},
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {label}
            {required && <span style={{ color: 'red', marginLeft: 4 }}>*</span>}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: value == null ? 'inherit' : 'monospace',
              color: value == null ? 'text.disabled' : 'text.primary',
              fontSize: '0.75rem',
              wordBreak: 'break-word',
            }}
          >
            {value == null ? <em>Не задано</em> : formatValue(value)}
          </Typography>
        </Box>
        {canEdit && typeof value !== 'object' && (
          <Tooltip title="Редактировать">
            <IconButton
              size="small"
              onClick={() => {
                setTempValue(value);
                setEditing(true);
              }}
              sx={{ p: 0.25 }}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Редактор данных (рекурсивный, с canEdit)
// ============================================================================
function DataEditor({ data, onChange, canEdit = true }) {
  const [localData, setLocalData] = useState(data);

  useEffect(() => setLocalData(data), [data]);

  const updateValue = (key, value) => {
    const next = { ...localData, [key]: value };
    setLocalData(next);
    onChange?.(next);
  };

  const updateNested = (key, value) => updateValue(key, value);

  const updateArrayItem = (arrayKey, index, value) => {
    const arr = [...localData[arrayKey]];
    arr[index] = value;
    updateValue(arrayKey, arr);
  };

  const addArrayItem = (arrayKey) => {
    const arr = localData[arrayKey] || [];
    updateValue(arrayKey, [...arr, {}]);
  };

  const removeArrayItem = (arrayKey, index) => {
    const arr = localData[arrayKey].filter((_, i) => i !== index);
    updateValue(arrayKey, arr);
  };

  if (!localData || typeof localData !== 'object') {
    return (
      <EditableField
        label="Значение"
        value={localData}
        onChange={(v) => onChange?.(v)}
        canEdit={canEdit}
      />
    );
  }

  const systemFields = [
    '_id', 'name', 'type', 'description', 'file_path', 'folder',
    'object_count', 'source', 'storage', 'created', 'updated', 'version',
  ];
  const editableFields = systemFields.filter((f) => f in localData);
  const dataFields = Object.keys(localData).filter((f) => !systemFields.includes(f));

  return (
    <Box>
      {editableFields.map((field) => (
        <EditableField
          key={field}
          label={
            field === 'name' ? 'Название'
            : field === 'type' ? 'Тип'
            : field === 'description' ? 'Описание'
            : field === '_id' ? 'ID'
            : field
          }
          value={localData[field]}
          onChange={(v) => updateValue(field, v)}
          multiline={field === 'description'}
          required={field === 'name'}
          canEdit={canEdit}
        />
      ))}

      {dataFields.map((key) => {
        const value = localData[key];

        if (Array.isArray(value)) {
          return (
            <Accordion key={key} sx={{ mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {key}
                  </Typography>
                  <Chip label={`${value.length}`} size="small" color="primary" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } }} />
                  {canEdit && (
                    <Button
                      size="small"
                      startIcon={<AddIcon fontSize="small" />}
                      onClick={(e) => { e.stopPropagation(); addArrayItem(key); }}
                      sx={{ ml: 'auto', fontSize: '0.65rem', py: 0, minHeight: 22 }}
                    >
                      Добавить
                    </Button>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {value.map((item, index) => (
                  <Accordion key={index} sx={{ mb: 0.5, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                          #{index + 1}
                          {item && typeof item === 'object' && item.name ? ` · ${item.name}` : ''}
                        </Typography>
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); removeArrayItem(key, index); }}
                            color="error"
                            sx={{ ml: 'auto', p: 0.25 }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 1 }}>
                      <DataEditor
                        data={item}
                        onChange={(v) => updateArrayItem(key, index, v)}
                        canEdit={canEdit}
                      />
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        }

        if (typeof value === 'object' && value !== null) {
          return (
            <Accordion key={key} sx={{ mb: 1, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                    {key}
                  </Typography>
                  <Chip label="Объект" size="small" color="secondary" sx={{ height: 16, '& .MuiChip-label': { fontSize: '0.55rem', px: 0.5 } }} />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                <DataEditor
                  data={value}
                  onChange={(v) => updateNested(key, v)}
                  canEdit={canEdit}
                />
              </AccordionDetails>
            </Accordion>
          );
        }

        let fieldType = 'text';
        if (typeof value === 'number') fieldType = 'number';
        if (typeof value === 'boolean') fieldType = 'boolean';

        return (
          <EditableField
            key={key}
            label={key}
            value={value}
            onChange={(v) => updateValue(key, v)}
            type={fieldType}
            canEdit={canEdit}
          />
        );
      })}
    </Box>
  );
}

// ============================================================================
// Просмотр JSON
// ============================================================================
function JsonView({ data, onCopy }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    onCopy?.('JSON скопирован');
  };
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
          JSON
        </Typography>
        <Tooltip title="Копировать">
          <IconButton onClick={handleCopy} size="small" sx={{ p: 0.25 }}>
            <CopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          p: 0.75,
          bgcolor: 'grey.50',
          maxHeight: '60vh',
          overflow: 'auto',
        }}
      >
        <pre style={{ margin: 0, fontSize: '0.65rem', fontFamily: 'monospace', lineHeight: 1.3 }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    </Box>
  );
}

// ============================================================================
// Главный компонент
// ============================================================================
export default function OntologyEditor() {
  const auth = useSelector((state) => state.auth);
  const isAuthenticated = auth?.isAuthenticated;
  const canEdit = isAdmin(auth);

  const [ontologyTree, setOntologyTree] = useState([]);
  const [currentOntology, setCurrentOntology] = useState(null);
  const [selectedPath, setSelectedPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newOntology, setNewOntology] = useState({ name: '', type: 'object', description: '' });

  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const loadTree = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const response = await Gateway.http.imcon.get('/api/ontology/tree');
      const treeData = response.data?.children || response.data;
      setOntologyTree(Array.isArray(treeData) ? treeData : [treeData]);
    } catch (error) {
      console.error('❌ Ошибка загрузки дерева:', error);
      showSnackbar('Ошибка загрузки дерева', 'error');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleSelectOntology = async (path, item) => {
    if (item.type !== 'file') return;
    setSelectedPath(path);
    setLoading(true);
    try {
      const response = await Gateway.http.imcon.get(`/api/ontology/${encodeURIComponent(path)}`);
      if (response.data) {
        setCurrentOntology(response.data);
        setActiveTab(0);
      }
    } catch (error) {
      console.error('❌ Ошибка загрузки онтологии:', error);
      showSnackbar(`Ошибка загрузки "${path}"`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOntologyChange = (newData) => {
    if (!canEdit) return;
    setCurrentOntology(newData);
  };

  const handleSave = async () => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentOntology) return;
    setSaving(true);
    try {
      const response = await Gateway.http.imcon.post('/api/ontology/save', {
        ...currentOntology,
        updated: new Date().toISOString(),
      });
      if (response.data) {
        showSnackbar('Онтология сохранена', 'success');
        loadTree();
      }
    } catch (error) {
      console.error('❌ Ошибка сохранения:', error);
      showSnackbar('Ошибка сохранения', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOntology = async () => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    if (!newOntology.name.trim()) return;
    const ontologyData = {
      name: newOntology.name,
      type: newOntology.type,
      description: newOntology.description,
      created: new Date().toISOString(),
      updated: new Date().toISOString(),
      version: '1.0.0',
      data: {},
    };
    try {
      const response = await Gateway.http.imcon.post('/api/ontology/create', ontologyData);
      if (response.data) {
        setCurrentOntology(ontologyData);
        setSelectedPath(newOntology.name);
        setCreateDialogOpen(false);
        setNewOntology({ name: '', type: 'object', description: '' });
        showSnackbar(`Онтология "${ontologyData.name}" создана`, 'success');
        loadTree();
      }
    } catch (error) {
      console.error('❌ Ошибка создания:', error);
      showSnackbar('Ошибка создания', 'error');
    }
  };

  const handleDeleteOntology = async () => {
    if (!canEdit) return showSnackbar('Недостаточно прав', 'warning');
    if (!currentOntology || !window.confirm('Удалить эту онтологию?')) return;
    try {
      await Gateway.http.imcon.delete(`/api/ontology/${encodeURIComponent(selectedPath)}`);
      setCurrentOntology(null);
      setSelectedPath('');
      showSnackbar('Онтология удалена', 'success');
      loadTree();
    } catch (error) {
      console.error('❌ Ошибка удаления:', error);
      showSnackbar('Ошибка удаления', 'error');
    }
  };

  const handleCopyJson = (message) => showSnackbar(message, 'success');

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  if (!isAuthenticated) {
    return (
      <PageShell>
        <PagePanel>
          <Box sx={{ p: 2 }}>
            <Alert severity="warning">Требуется авторизация</Alert>
          </Box>
        </PagePanel>
      </PageShell>
    );
  }

  // ==========================================================================
  // Панель действий
  // ==========================================================================
  const actionsPanel = (
    <PagePanel sx={{ flexShrink: 0 }}>
      <Box sx={{ py: 0.75, px: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ rowGap: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon fontSize="small" />}
            onClick={loadTree}
            disabled={loading}
            sx={{ fontSize: '0.7rem', py: 0.5 }}
          >
            Обновить
          </Button>

          <Box sx={{ flex: 1 }} />

          {canEdit && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ fontSize: '0.7rem', py: 0.5 }}
            >
              Создать онтологию
            </Button>
          )}
        </Stack>
      </Box>
    </PagePanel>
  );

  // ==========================================================================
  // JSX
  // ==========================================================================
  return (
    <PageShell>
      {/* Шапка */}
      <PagePanel sx={{ flexShrink: 0 }}>
        <Box sx={{ py: 1, px: 1.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={1} justifyContent="space-between">
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SchemaIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ fontSize: '1.05rem' }}>
                  Редактор онтологий
                </Typography>
                {!canEdit && (
                  <Chip
                    icon={<LockIcon sx={{ fontSize: 12 }} />}
                    label="Только просмотр"
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.6rem' } }}
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                Структура данных и онтологии игрового мира
              </Typography>
            </Box>
          </Stack>
        </Box>
      </PagePanel>

      {actionsPanel}

      {/* Контент: дерево + редактор */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          gap: 0.75,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Левая колонка — дерево */}
        <PagePanel sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
              Структура
            </Typography>
          </Box>
          <Box sx={{ px: 1, pt: 1 }}>
            <TextField
              placeholder="Поиск..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />,
                sx: { fontSize: '0.75rem' },
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, mt: 0.5 }}>
            <OntologyTree
              treeData={ontologyTree}
              onSelect={handleSelectOntology}
              selectedPath={selectedPath}
              loading={loading}
              searchTerm={searchTerm}
            />
          </Box>
        </PagePanel>

        {/* Правая колонка — редактор */}
        <PagePanel sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 1 }}>
            {currentOntology ? (
              <>
                {/* Заголовок онтологии */}
                <Box sx={{ mb: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                    <Typography variant="h6" sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
                      {currentOntology.name}
                    </Typography>
                    {currentOntology.type && (
                      <Chip
                        label={ONTOLOGY_TYPES[currentOntology.type]?.label || currentOntology.type}
                        size="small"
                        color="primary"
                        sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                      />
                    )}
                    {currentOntology.object_count > 0 && (
                      <Chip
                        label={`${currentOntology.object_count} эл.`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                      />
                    )}
                    {currentOntology.version && (
                      <Chip
                        label={`v${currentOntology.version}`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.6rem', px: 0.75 } }}
                      />
                    )}
                  </Stack>
                  {currentOntology.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                      {currentOntology.description}
                    </Typography>
                  )}
                  {selectedPath && (
                    <BreadcrumbNavigation
                      path={selectedPath}
                      onPathClick={(path) => console.log('Navigate to:', path)}
                    />
                  )}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    {canEdit && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ fontSize: '0.7rem', py: 0.25 }}
                      >
                        Сохранить
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon fontSize="small" />}
                        onClick={handleDeleteOntology}
                        sx={{ fontSize: '0.7rem', py: 0.25 }}
                      >
                        Удалить
                      </Button>
                    )}
                    {currentOntology.created && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center', fontSize: '0.65rem' }}>
                        Создано: {new Date(currentOntology.created).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Divider sx={{ mb: 1 }} />

                {/* Вкладки */}
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{
                    minHeight: 30,
                    mb: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      minHeight: 30,
                      fontSize: '0.7rem',
                      textTransform: 'none',
                      py: 0.25,
                      px: 1,
                      minWidth: 'auto',
                    },
                  }}
                >
                  <Tab icon={<VisibilityIcon fontSize="small" />} iconPosition="start" label="Просмотр" />
                  <Tab icon={<CodeIcon fontSize="small" />} iconPosition="start" label="JSON" />
                </Tabs>

                {activeTab === 0 && (
                  <DataEditor
                    data={currentOntology}
                    onChange={handleOntologyChange}
                    canEdit={canEdit}
                  />
                )}
                {activeTab === 1 && (
                  <JsonView data={currentOntology} onCopy={handleCopyJson} />
                )}
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  py: 4,
                }}
              >
                <StorageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1, opacity: 0.3 }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Выберите онтологию из дерева слева
                </Typography>
                {canEdit && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{ mt: 2, fontSize: '0.7rem' }}
                  >
                    Создать новую
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </PagePanel>
      </Box>

      {/* Диалог создания (только для canEdit) */}
      {canEdit && (
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
            <AddIcon sx={{ mr: 1, fontSize: 18 }} />
            Создать онтологию
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Название"
                value={newOntology.name}
                onChange={(e) => setNewOntology({ ...newOntology, name: e.target.value })}
                fullWidth
                size="small"
                required
                helperText="Уникальное название"
                sx={{ '& input': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Тип</InputLabel>
                <Select
                  value={newOntology.type}
                  label="Тип"
                  onChange={(e) => setNewOntology({ ...newOntology, type: e.target.value })}
                  sx={{ fontSize: '0.8rem' }}
                >
                  {Object.entries(ONTOLOGY_TYPES).map(([value, config]) => (
                    <MenuItem key={value} value={value} sx={{ fontSize: '0.8rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{config.icon}</span>
                        <span>{config.label}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Описание"
                multiline
                rows={3}
                value={newOntology.description}
                onChange={(e) => setNewOntology({ ...newOntology, description: e.target.value })}
                fullWidth
                size="small"
                helperText="Краткое описание"
                sx={{ '& textarea': { fontSize: '0.8rem' }, '& .MuiFormHelperText-root': { fontSize: '0.65rem' } }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateDialogOpen(false)} size="small" sx={{ fontSize: '0.7rem' }}>
              Отмена
            </Button>
            <Button
              onClick={handleCreateOntology}
              variant="contained"
              size="small"
              disabled={!newOntology.name.trim()}
              sx={{ fontSize: '0.7rem' }}
            >
              Создать
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </PageShell>
  );
}